import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Container } from "../../components/ui/Container";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Spinner } from "../../components/ui/Spinner";
import { Alert } from "../../components/ui/Alert";
import { Input } from "../../components/ui/Input";
import { Select } from "../../components/ui/Select";
import { Text } from "../../components/ui/Text";
import { Table, TableColumn } from "../../components/ui/Table";
import { api } from "../../services/api";
import { FaCheck, FaChevronDown, FaChevronUp } from "react-icons/fa";

interface ContributorPayout {
  _id: string;
  contributorId?: {
    _id: string;
    name: string;
    email?: string;
  };
  periodStart: string;
  periodEnd: string;
  totalAmount: number;
  currency: string;
  status: "pending" | "paid" | "cancelled";
  paidAt?: string;
  paidBy?: {
    _id: string;
    name: string;
  };
  metadata?: {
    countryCode?: string;
    storeOwnerId?: string;
  };
}

interface Location {
  _id: string;
  country: string;
  countryCode: string;
  currency: string;
}

export const Payouts: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [payouts, setPayouts] = useState<ContributorPayout[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedCurrency, setSelectedCurrency] = useState<string>("USD");
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const [periodStart, setPeriodStart] = useState<string>("");
  const [periodEnd, setPeriodEnd] = useState<string>("");
  const [expandedPayout, setExpandedPayout] = useState<string | null>(null);

  useEffect(() => {
    loadLocations();
    loadPayouts();
  }, []);

  const loadLocations = async () => {
    try {
      const response = await api.get("/locations/admin");
      setLocations(response.data.locations || []);
    } catch (err: any) {
      console.error("Failed to load locations:", err);
      setLocations([]);
    }
  };

  const loadPayouts = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {};
      if (selectedCountry) params.countryCode = selectedCountry;
      if (periodStart) params.periodStart = periodStart;
      if (periodEnd) params.periodEnd = periodEnd;

      const response = await api.get("/profit-rules/payouts", { params });
      setPayouts(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || t("admin.payouts.errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleCalculatePayouts = async () => {
    if (!periodStart || !periodEnd) {
      setError(t("admin.payouts.errors.selectPeriod"));
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await api.post("/profit-rules/payouts/calculate", {
        periodStart,
        periodEnd,
        countryCode: selectedCountry || undefined,
      });
      setPayouts(response.data);
      setSuccess(t("admin.payouts.success.calculated"));
    } catch (err: any) {
      setError(err.response?.data?.error || t("admin.payouts.errors.calculateFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleMarkAsPaid = async (payoutId: string) => {
    if (!window.confirm(t("admin.payouts.confirm.markPaid"))) return;

    setSaving(true);
    setError(null);
    try {
      await api.put(`/profit-rules/payouts/${payoutId}/mark-paid`);
      await loadPayouts();
      setSuccess(t("admin.payouts.success.markedPaid"));
    } catch (err: any) {
      setError(err.response?.data?.error || t("admin.payouts.errors.markPaidFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleExpandPayout = async (payoutId: string) => {
    if (expandedPayout === payoutId) {
      setExpandedPayout(null);
      return;
    }

    setExpandedPayout(payoutId);
    // Load profit details for this payout
    try {
      // This would need a new endpoint to get profit details for a payout
      // For now, we'll just show the payout info
    } catch (err: any) {
      console.error("Failed to load payout details:", err);
    }
  };

  // Calculate net payouts (differences when contributors pay each other)
  const calculateNetPayouts = () => {
    const netPayouts: Record<string, { amount: number; currency: string }> = {};

    for (const payout of payouts) {
      const key = payout.contributorId?._id || `store-owner-${payout.metadata?.storeOwnerId || "unknown"}`;
      if (!netPayouts[key]) {
        netPayouts[key] = { amount: 0, currency: payout.currency };
      }

      // Convert to selected currency (simplified - would need exchange rates)
      let amount = payout.totalAmount;
      if (payout.currency !== selectedCurrency) {
        // Currency conversion would go here
        // For now, just use the amount as-is
      }

      netPayouts[key].amount += amount;
    }

    return netPayouts;
  };

  const netPayouts = calculateNetPayouts();

  // Get unique currencies
  const currencies = Array.from(new Set(payouts.map((p) => p.currency)));

  const columns: TableColumn<ContributorPayout>[] = [
    {
      key: "contributor",
      header: t("admin.payouts.columns.contributor"),
      render: (payout) => payout.contributorId?.name || t("admin.payouts.storeOwner"),
    },
    {
      key: "amount",
      header: t("admin.payouts.columns.amount"),
      render: (payout) => {
        const amount = payout.totalAmount;
        const currency = payout.currency;
        // Convert to selected currency if needed (simplified)
        return `${currency} ${amount.toFixed(2)}`;
      },
    },
    {
      key: "status",
      header: t("admin.payouts.columns.status"),
      render: (payout) => (
        <span
          className={`px-2 py-1 rounded text-xs ${
            payout.status === "paid"
              ? "bg-green-100 text-green-800"
              : payout.status === "cancelled"
              ? "bg-red-100 text-red-800"
              : "bg-yellow-100 text-yellow-800"
          }`}
        >
          {t(`admin.payouts.status.${payout.status}`)}
        </span>
      ),
    },
    {
      key: "period",
      header: t("admin.payouts.columns.period"),
      render: (payout) => {
        const start = new Date(payout.periodStart).toLocaleDateString();
        const end = new Date(payout.periodEnd).toLocaleDateString();
        return `${start} - ${end}`;
      },
    },
    {
      key: "actions",
      header: t("admin.payouts.columns.actions"),
      render: (payout) => (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleExpandPayout(payout._id)}
          >
            {expandedPayout === payout._id ? <FaChevronUp /> : <FaChevronDown />}
          </Button>
          {payout.status === "pending" && (
            <Button
              size="sm"
              onClick={() => handleMarkAsPaid(payout._id)}
              disabled={saving}
            >
              <FaCheck className="mr-1" />
              {t("admin.payouts.markPaid")}
            </Button>
          )}
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <Container>
        <Stack spacing="lg">
          <Spinner />
        </Stack>
      </Container>
    );
  }

  return (
    <Container>
      <Stack spacing="lg">
        <Heading size="xl">{t("admin.payouts.title")}</Heading>
        <Text className="text-gray-600 dark:text-gray-400">
          {t("admin.payouts.description")}
        </Text>

        {error && <Alert variant="error">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        {/* Filters and Controls */}
        <Card padding="lg">
          <Stack spacing="md">
            <Heading size="md">{t("admin.payouts.calculate.title")}</Heading>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Input
                label={t("admin.payouts.periodStart")}
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
              />
              <Input
                label={t("admin.payouts.periodEnd")}
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
              />
              <Select
                label={t("admin.payouts.countryOptional")}
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                options={[
                  { value: "", label: t("admin.payouts.allCountries") },
                  ...(Array.isArray(locations) ? locations.map((loc) => ({
                    value: loc.countryCode,
                    label: loc.country,
                  })) : []),
                ]}
              />
              <div className="flex items-end">
                <Button onClick={handleCalculatePayouts} disabled={saving || !periodStart || !periodEnd}>
                  {t("admin.payouts.calculate.button")}
                </Button>
              </div>
            </div>
          </Stack>
        </Card>

        {/* Currency Selector */}
        {currencies.length > 1 && (
          <Card padding="md">
            <div className="flex items-center gap-4">
              <Text className="font-medium">{t("admin.payouts.viewInCurrency")}:</Text>
              <Select
                value={selectedCurrency}
                onChange={(e) => setSelectedCurrency(e.target.value)}
                options={currencies.map((curr) => ({ value: curr, label: curr }))}
              />
            </div>
          </Card>
        )}

        {/* Net Payouts Summary */}
        {Object.keys(netPayouts).length > 0 && (
          <Card padding="lg">
            <Stack spacing="md">
              <Heading size="md">{t("admin.payouts.netPayouts")}</Heading>
              <div className="space-y-2">
                {Object.entries(netPayouts).map(([key, net]) => (
                  <div key={key} className="flex justify-between p-3 border border-gray-200 dark:border-gray-700 rounded">
                    <Text className="font-medium">{key.includes("store-owner") ? t("admin.payouts.storeOwner") : key}</Text>
                    <Text className="font-medium">
                      {net.currency} {net.amount.toFixed(2)}
                    </Text>
                  </div>
                ))}
              </div>
            </Stack>
          </Card>
        )}

        {/* Payouts Table */}
        <Card padding="lg">
          <Stack spacing="md">
            <Heading size="md">{t("admin.payouts.details.title")}</Heading>
            {payouts.length === 0 ? (
              <Text className="text-gray-500">{t("admin.payouts.noPayouts")}</Text>
            ) : (
              <>
                <Table columns={columns} data={payouts} />
                {payouts.map((payout) => (
                  expandedPayout === payout._id && (
                    <Card key={payout._id} padding="md" className="mt-4">
                      <Stack spacing="sm">
                        <Heading size="sm">{t("admin.payouts.details.title")}</Heading>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <Text className="font-medium">{t("admin.payouts.details.contributor")}:</Text>
                            <Text>{payout.contributorId?.name || t("admin.payouts.storeOwner")}</Text>
                          </div>
                          <div>
                            <Text className="font-medium">{t("admin.payouts.details.amount")}:</Text>
                            <Text>
                              {payout.currency} {payout.totalAmount.toFixed(2)}
                            </Text>
                          </div>
                          <div>
                            <Text className="font-medium">{t("admin.payouts.details.status")}:</Text>
                            <Text>{t(`admin.payouts.status.${payout.status}`)}</Text>
                          </div>
                          {payout.paidAt && (
                            <div>
                              <Text className="font-medium">{t("admin.payouts.details.paidAt")}:</Text>
                              <Text>{new Date(payout.paidAt).toLocaleString()}</Text>
                            </div>
                          )}
                          {payout.paidBy && (
                            <div>
                              <Text className="font-medium">{t("admin.payouts.details.paidBy")}:</Text>
                              <Text>{payout.paidBy.name}</Text>
                            </div>
                          )}
                        </div>
                      </Stack>
                    </Card>
                  )
                ))}
              </>
            )}
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
};
